import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { CalendarBlank, ChartBar, CreditCard, Handshake, Printer, Wallet, Buildings, Bank, TrendUp } from '@phosphor-icons/react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const fmt = (n) => `Rs. ${new Intl.NumberFormat('en-NP').format(Math.round(n || 0))}`;

function todayString() {
  return new Date().toISOString().slice(0, 10);
}

function monthStartString() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
}

export default function ReportsPage() {
  const [fromDate, setFromDate] = useState(monthStartString);
  const [toDate, setToDate] = useState(todayString);
  const [draftFromDate, setDraftFromDate] = useState(monthStartString);
  const [draftToDate, setDraftToDate] = useState(todayString);
  const [section, setSection] = useState('transactions');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get('/reports/summary', { params: { from_date: fromDate, to_date: toDate } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [fromDate, toDate]);

  const handleFilter = (e) => {
    e.preventDefault();
    setFromDate(draftFromDate);
    setToDate(draftToDate);
  };

  const summaryCards = useMemo(() => {
    const d = data || {};

    return [
      { icon: Wallet, label: 'Income', value: fmt(d.total_income), color: '#265C42' },
      { icon: Wallet, label: 'Expenses', value: fmt(d.total_expenses), color: '#A63C24' },
      { icon: ChartBar, label: 'Net Cashflow', value: fmt(d.net_cashflow), color: d.net_cashflow >= 0 ? '#1E3F2D' : '#A63C24' },
      { icon: TrendUp, label: 'Net Worth', value: fmt(d.net_worth), color: '#1E3F2D' },
    ];
  }, [data]);

  const sections = useMemo(() => {
    const d = data || {};

    return {
      transactions: {
        title: 'Transactions Report',
        description: 'Income and expense entries in the selected date range.',
        headers: ['Date', 'Type', 'Category', 'Description', 'Account', 'Amount'],
        rows: (d.transactions || []).map((item) => [item.date, item.type, item.category, item.description || '-', item.account || '-', fmt(item.amount)]),
      },
      stocks: {
        title: 'Stock Trades Report',
        description: 'Buy and sell trade activity for the selected period.',
        headers: ['Date', 'Type', 'Symbol', 'Company', 'Qty', 'Price', 'Total', 'Commission'],
        rows: (d.stock_trades || []).map((item) => [item.date, item.type, item.symbol, item.company_name || '-', item.quantity, fmt(item.price_per_unit), fmt(item.total_cost), fmt(item.commission)]),
      },
      payments: {
        title: 'Payments Report',
        description: 'Paid and pending payment entries with next recurring dates.',
        headers: ['Name', 'Status', 'Amount', 'Due Date', 'Paid Date', 'Next Date'],
        rows: (d.payments || []).map((item) => [item.name, item.status, fmt(item.amount), item.due_date || '-', item.paid_date || '-', item.next_payment_date || '-']),
      },
      lendborrow: {
        title: 'Lend / Borrow Report',
        description: 'Money lent out or borrowed in the selected period.',
        headers: ['Date', 'Type', 'Person', 'Amount', 'Paid', 'Remaining', 'Status'],
        rows: (d.lend_borrow || []).map((item) => [item.date, item.type, item.person_name, fmt(item.amount), fmt(item.paid_amount), fmt(item.remaining_amount), item.status]),
      },
      emi: {
        title: 'EMI Report',
        description: 'Upcoming EMI obligations and due dates.',
        headers: ['Loan', 'EMI Amount', 'Next Payment Date'],
        rows: (d.emi_schedule || []).map((item) => [item.loan_name, fmt(item.emi_amount), item.next_payment_date || '-']),
      },
      accounts: {
        title: 'Accounts Report',
        description: 'Current account snapshot used by the financial summary.',
        headers: ['Account', 'Type', 'Branch', 'Balance'],
        rows: (d.banks || []).map((item) => [item.bank_name, item.account_type, item.branch || '-', fmt(item.balance)]),
      },
      properties: {
        title: 'Properties Report',
        description: 'Property and asset snapshot values.',
        headers: ['Name', 'Type', 'Location', 'Value'],
        rows: (d.properties || []).map((item) => [item.name, item.property_type, item.location || '-', fmt(item.value)]),
      },
    };
  }, [data]);

  const printSection = () => {
    const currentSection = sections[section];
    const periodLabel = data?.period?.label || '';

    if (!currentSection) return;

    const reportWindow = window.open('', '_blank', 'width=980,height=760');
    if (!reportWindow) return;

    const rows = currentSection.rows.length
      ? currentSection.rows.map((row) => `<tr>${row.map((cell) => `<td>${cell}</td>`).join('')}</tr>`).join('')
      : `<tr><td colspan="${currentSection.headers.length}">No data in this date range</td></tr>`;

    reportWindow.document.write(`
      <html>
        <head>
          <title>${currentSection.title}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 24px; color: #1c1b1a; }
            h1, p { margin: 0 0 12px; }
            .summary { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin: 20px 0; }
            .card { border: 1px solid #d8d4cc; border-radius: 10px; padding: 12px; }
            .label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.12em; color: #5c5a56; }
            .value { font-size: 18px; font-weight: 700; margin-top: 4px; }
            table { width: 100%; border-collapse: collapse; margin-top: 18px; }
            th, td { border: 1px solid #d8d4cc; padding: 8px 10px; text-align: left; font-size: 12px; }
            th { background: #f5f2ed; }
          </style>
        </head>
        <body>
          <h1>${currentSection.title}</h1>
          <p>${periodLabel}</p>
          <p>${currentSection.description}</p>
          <div class="summary">
            ${summaryCards.map((item) => `<div class="card"><div class="label">${item.label}</div><div class="value">${item.value}</div></div>`).join('')}
          </div>
          <table>
            <thead><tr>${currentSection.headers.map((header) => `<th>${header}</th>`).join('')}</tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </body>
      </html>
    `);
    reportWindow.document.close();
    reportWindow.focus();
    reportWindow.print();
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-3 border-[#1E3F2D] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const d = data || {};

  return (
    <div className="space-y-5 animate-fade-in" data-testid="reports-page">
      <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1C1B1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>Financial Reports</h1>
          <p className="text-[#5C5A56] text-sm mt-1">Choose one section at a time, filter by date range, then print that section to PDF.</p>
        </div>
        <form onSubmit={handleFilter} className="flex flex-col sm:flex-row gap-3">
          <div>
            <label htmlFor="reports-from-date" className="block text-xs font-bold uppercase tracking-[0.16em] text-[#5C5A56] mb-2">From Date</label>
            <input id="reports-from-date" name="from_date" type="date" value={draftFromDate} onChange={(e) => setDraftFromDate(e.target.value)} className="bg-white border border-[#E8E6E1] rounded-lg px-3 py-2.5 text-sm w-full sm:w-44" />
          </div>
          <div>
            <label htmlFor="reports-to-date" className="block text-xs font-bold uppercase tracking-[0.16em] text-[#5C5A56] mb-2">To Date</label>
            <input id="reports-to-date" name="to_date" type="date" value={draftToDate} onChange={(e) => setDraftToDate(e.target.value)} className="bg-white border border-[#E8E6E1] rounded-lg px-3 py-2.5 text-sm w-full sm:w-44" />
          </div>
          <div className="flex items-end gap-3">
            <button type="submit" className="bg-[#1E3F2D] text-white font-semibold rounded-lg px-4 py-2.5 text-sm">Apply Filter</button>
            <button type="button" onClick={printSection} className="bg-white border border-[#E8E6E1] text-[#1C1B1A] font-semibold rounded-lg px-4 py-2.5 text-sm flex items-center gap-2">
              <Printer size={16} weight="bold" /> Print PDF
            </button>
          </div>
        </form>
      </div>

      <div className="bg-gradient-to-br from-[#1E3F2D] to-[#265C42] rounded-2xl shadow-sm p-6 text-white">
        <p className="text-sm font-bold uppercase tracking-[0.2em] opacity-70">Report Period</p>
        <p className="text-3xl font-bold mt-2">{d.period?.label || '-'}</p>
        <p className="text-sm opacity-70 mt-2">Assets: {fmt(d.total_assets)} | Liabilities: {fmt(d.total_liabilities)} | Net Worth: {fmt(d.net_worth)}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {summaryCards.map((item) => (
          <SummaryCard key={item.label} icon={item.icon} label={item.label} value={item.value} color={item.color} />
        ))}
      </div>

      <Tabs value={section} onValueChange={setSection} className="space-y-4">
        <TabsList className="w-full h-auto p-1.5 bg-[#F1ECE4] rounded-2xl flex flex-wrap justify-start">
          <TabsTrigger value="transactions" className="rounded-xl data-[state=active]:bg-white">Transactions</TabsTrigger>
          <TabsTrigger value="stocks" className="rounded-xl data-[state=active]:bg-white">Stocks</TabsTrigger>
          <TabsTrigger value="payments" className="rounded-xl data-[state=active]:bg-white">Payments</TabsTrigger>
          <TabsTrigger value="lendborrow" className="rounded-xl data-[state=active]:bg-white">Lend / Borrow</TabsTrigger>
          <TabsTrigger value="emi" className="rounded-xl data-[state=active]:bg-white">EMI</TabsTrigger>
          <TabsTrigger value="accounts" className="rounded-xl data-[state=active]:bg-white">Accounts</TabsTrigger>
          <TabsTrigger value="properties" className="rounded-xl data-[state=active]:bg-white">Properties</TabsTrigger>
        </TabsList>

        {Object.entries(sections).map(([key, current]) => (
          <TabsContent key={key} value={key} className="mt-0">
            <SectionHeader sectionKey={key} title={current.title} description={current.description} />
            <DataTable title={current.title} headers={current.headers} rows={current.rows} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function SectionHeader({ sectionKey, title, description }) {
  const icons = {
    transactions: Wallet,
    stocks: ChartBar,
    payments: CreditCard,
    lendborrow: Handshake,
    emi: CalendarBlank,
    accounts: Bank,
    properties: Buildings,
  };

  const Icon = icons[sectionKey] || ChartBar;

  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-11 h-11 rounded-2xl bg-[#1E3F2D]/10 flex items-center justify-center">
        <Icon size={20} weight="fill" className="text-[#1E3F2D]" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-[#1C1B1A]">{title}</h2>
        <p className="text-sm text-[#5C5A56]">{description}</p>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#5C5A56]">{label}</span>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon size={16} weight="duotone" style={{ color }} />
        </div>
      </div>
      <p className="text-lg font-bold font-mono-numbers" style={{ color }}>{value}</p>
    </div>
  );
}

function DataTable({ title, headers, rows }) {
  return (
    <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 bg-[#FBF9F6] border-b border-[#E8E6E1]">
        <h3 className="text-sm font-semibold text-[#1C1B1A]">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white">
            <tr>
              {headers.map((header) => (
                <th key={header} className="px-4 py-2 text-left text-xs font-bold uppercase tracking-[0.12em] text-[#5C5A56]">{header}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E8E6E1]">
            {rows.length === 0 ? (
              <tr><td colSpan={headers.length} className="px-4 py-5 text-center text-[#5C5A56]">No data in this date range</td></tr>
            ) : rows.map((row, index) => (
              <tr key={`${title}-${index}`} className="bg-white">
                {row.map((cell, cellIndex) => (
                  <td key={`${title}-${index}-${cellIndex}`} className="px-4 py-3 text-[#1C1B1A] whitespace-nowrap">{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
